import { defineComponent, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
// import './src/index.less';
// import { rgbToHex } from './src/util'; //rgb转十六进制
import { CAD_COLOR_CONVERTER, rgbToHex } from '/@/utils/color';
import { Plugin } from '@vue/runtime-dom';
import './index.less';

/** 白色 / 近白：色块需加灰边框，避免与浅色背景融为一体 */
function isNearWhiteColor(hex?: string, colorIndex?: number) {
  if (colorIndex === 7 || colorIndex === 0 || colorIndex === -1 || colorIndex === 255) {
    return true;
  }
  if (!hex) return false;
  const normalized = hex.trim().toLowerCase();
  if (normalized === 'white' || normalized === '#fff' || normalized === '#ffffff') {
    return true;
  }
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(normalized);
  if (!match) return false;
  const raw = match[1];
  let r: number;
  let g: number;
  let b: number;
  if (raw.length === 3) {
    r = parseInt(raw[0] + raw[0], 16);
    g = parseInt(raw[1] + raw[1], 16);
    b = parseInt(raw[2] + raw[2], 16);
  } else {
    r = parseInt(raw.slice(0, 2), 16);
    g = parseInt(raw.slice(2, 4), 16);
    b = parseInt(raw.slice(4, 6), 16);
  }
  return r >= 250 && g >= 250 && b >= 250;
}

const comp = defineComponent({
  props: {
    hex: { type: String, default: '' }, //16进制颜色表示
    colorIndex: { type: Number, default: 7 }, //颜色下标索引
    /** 设计稿样式：16px 色块 +「切换」链接 */
    showSwitchLink: { type: Boolean, default: false },
  },
  emits: ['update:hex', 'update:colorIndex'],

  setup(props, { emit }) {
    const firstColumn = [18, 16, 14, 12, 10, 11, 13, 15, 17, 19];
    const column = 24;
    const colors: number[][] = [];

    // 创建二维数组按序添加CAD所有颜色
    for (const fc of firstColumn) {
      const cs: number[] = [];
      for (let i = 0; i < column; i++) {
        cs.push(fc + 10 * i);
      }
      colors.push(cs); //[key]
    }

    // 获取第一个画布内所有颜色的[key, value]键值对，优先级最低
    const getFirstRgbIndex = (arr: number[][]) => {
      const firstRowIndex: number[] = [];
      arr.map((item, index) => {
        for (let i = 0; i < item.length; i++) {
          firstRowIndex[index * column + i] = item[i];
        }
      });
      return firstRowIndex;
    };

    const firstRowIndex = getFirstRgbIndex(colors);
    const secondRowIndex = [1, 2, 3, 4, 5, 6, 7, 8, 9]; // 第二行颜色索引，优先级最高
    const thirdRowIndex = [250, 251, 252, 253, 254, 255]; //第三行颜色索引，优先级次级

    // 创建接口，包含颜色hex和rgb表示
    interface IArr {
      hex: string;
      rgb: number[];
    }

    // 使用接口按规范获取CAD所有颜色hex和rgb表示
    const getData = (indexArr: number[]) => {
      let arr: IArr[] = [];
      return (arr = indexArr.map((item: number) => {
        const rgb = CAD_COLOR_CONVERTER[item];
        const hex = rgbToHex(rgb[0], rgb[1], rgb[2]);
        return {
          hex,
          rgb,
        };
      }));
    };

    const firstRowData = getData(firstRowIndex); // 第一张画布所有颜色数据，格式[{hex:'',rgb:[]}]
    const secondRowData = getData(secondRowIndex); // 第二行所有颜色数据
    const thirdRowData = getData(thirdRowIndex); // 第三行所有颜色数据
    const totalColor = firstRowData.concat(secondRowData, thirdRowData); //汇总所有颜色数据

    const colorMap = new Map();
    totalColor.forEach((item: IArr) => {
      colorMap.set(item.hex, item.rgb);
    });

    const currentHex = ref(props.hex); // 响应式获取当前hex表示
    const currentRGB = ref(colorMap.get(currentHex.value)); //当前rgb表示
    const currentR = ref(currentRGB.value?.[0] ?? 255); //当前RGB中R
    const currentG = ref(currentRGB.value?.[1] ?? 255); //当前RGB中G
    const currentB = ref(currentRGB.value?.[2] ?? 255); //当前RGB中B
    const currentColorIndex = ref<number>(props.colorIndex); //当前颜色索引
    const open = ref(false); //是否打开颜色选择面板
    const openRGBInput = ref(false); //是否打开RGB输入确定面板，可以根据用户输入数据进行操作

    // 记录当前R、G、B的值，不是每改一个就响应式更新索引，而是全部RGB数据一起提交后进行响应
    const inputRGB: IArr = { hex: currentHex.value, rgb: currentRGB.value };

    watch(
      () => [props.hex, props.colorIndex] as const,
      ([hex, colorIndex]) => {
        currentHex.value = hex;
        currentColorIndex.value = colorIndex;
        const rgb =
          CAD_COLOR_CONVERTER[String(colorIndex)] ??
          colorMap.get(hex) ??
          CAD_COLOR_CONVERTER['7'];
        currentRGB.value = rgb;
        currentR.value = rgb[0];
        currentG.value = rgb[1];
        currentB.value = rgb[2];
        if (!hex) {
          currentHex.value = rgbToHex(rgb[0], rgb[1], rgb[2]);
        }
      }
    );

    // 展示颜色选择面板的操作
    const showModal = (flag: boolean) => {
      open.value = flag;
      if (!flag) {
        emit('update:hex', props.hex);
        emit('update:colorIndex', props.colorIndex);
      }
    };

    // 错误提示：不合法的颜色输入（索引和RGB值都要 >=0 && < 256)
    const errorMessage = () => {
      const rgbColor = CAD_COLOR_CONVERTER['3'];
      currentRGB.value = rgbColor;
      currentHex.value = rgbToHex(rgbColor[0], rgbColor[1], rgbColor[2]);
      message.error('颜色无效');
      return;
    };

    // 根据用户提示寻找最近的颜色
    const findNearestColorIndex = (): number => {
      let option = {
        min: Number.MAX_SAFE_INTEGER,
        colorIndex: -1,
      };
      const h1: number = currentR.value;
      const h2: number = currentG.value;
      const h3: number = currentB.value;
      for (const [k, v] of Object.entries(CAD_COLOR_CONVERTER)) {
        let colorIndex = +k;
        const [c1, c2, c3] = v;
        const sub1: number = Math.abs(c1 - h1);
        const sub2: number = Math.abs(c2 - h2);
        const sub3: number = Math.abs(c3 - h3);
        const total: number = sub1 + sub2 + sub3;

        if (total < option.min) {
          option = {
            min: total,
            colorIndex,
          };
        }
        //根据优先级选择展示索引值，优先展示优先级高的索引
        else if (total === option.min) {
          if (!secondRowIndex.includes(Number(option.colorIndex))) {
            if (secondRowIndex.includes(Number(k))) {
              colorIndex = Number(k);
              option = {
                min: total,
                colorIndex,
              };
            } else if (!thirdRowIndex.includes(Number(option.colorIndex))) {
              if (thirdRowIndex.includes(Number(k))) {
                colorIndex = Number(k);
                option = {
                  min: total,
                  colorIndex,
                };
              }
            }
          }
        }
      }
      return option.colorIndex;
    };

    // 搜索颜色索引，判断当前输入的RGB颜色是否存在，返回值为string，没找到为''
    const findColorIndex = (R: number, G: number, B: number) => {
      let isFind = '';
      for (const [key, value] of Object.entries(CAD_COLOR_CONVERTER)) {
        if (value[0] === R && value[1] === G && value[2] === B) {
          isFind = key;
          if (secondRowIndex.includes(Number(key)) || thirdRowIndex.includes(Number(key))) {
            break;
          }
        }
      }
      return isFind;
    };

    const handlerClick = (item: IArr, flag?: boolean) => {
      //点击  激活样式 判断是否有确认按钮 无则向父组件传递
      let index: any = null;

      // flag 判断是否是第一行，是第一行就不搜索后面两行
      if (!flag) {
        Object.entries(CAD_COLOR_CONVERTER).forEach(([key, value]) => {
          if (value === item.rgb) {
            index = key;
          }
        });
      } else {
        for (const [key, value] of Object.entries(CAD_COLOR_CONVERTER)) {
          if (value[0] === item.rgb[0] && value[1] === item.rgb[1] && value[2] === item.rgb[2]) {
            index = key;
            if (secondRowIndex.includes(Number(key)) || thirdRowIndex.includes(Number(key))) {
              break;
            }
          }
        }
      }

      // 如果没有找到，寻找最近邻下标并更新相应数据
      if (index === null) {
        const nearestColorIndex: number = findNearestColorIndex();
        currentColorIndex.value = nearestColorIndex;
        const rgbColor: number[] = CAD_COLOR_CONVERTER[currentColorIndex.value];
        currentR.value = rgbColor[0];
        currentG.value = rgbColor[1];
        currentB.value = rgbColor[2];
        currentRGB.value = rgbColor;
        currentHex.value = rgbToHex(rgbColor[0], rgbColor[1], rgbColor[2]);
      }
      // 找到直接更新数据
      else {
        currentColorIndex.value = Number(index);
        currentR.value = item.rgb[0];
        currentG.value = item.rgb[1];
        currentB.value = item.rgb[2];
        currentHex.value = item.hex;
        currentRGB.value = item.rgb;
      }
    };

    // 设置默认值，默认白色，索引为7，用户输入RGB不点击确定，点击取消或者关闭确定RGB输入面板时生效
    const setDefaultColor = () => {
      currentR.value = 255;
      currentG.value = 255;
      currentB.value = 255;
      currentRGB.value = [255, 255, 255];
      currentHex.value = rgbToHex(currentR.value, currentG.value, currentB.value);
      currentColorIndex.value = 7;
    };

    const confirmClick = () => {
      emit('update:hex', currentHex.value);
      emit('update:colorIndex', currentColorIndex.value);
      open.value = false;
    };

    return {
      firstRowData,
      secondRowData,
      thirdRowData,
      currentHex,
      currentRGB,
      currentR,
      currentG,
      currentB,
      open,
      showModal,
      handlerClick,
      confirmClick,
      currentColorIndex,
      errorMessage,
      findNearestColorIndex,
      inputRGB,
      openRGBInput,
      findColorIndex,
      setDefaultColor,
      isNearWhiteColor,
    };
  },
  render() {
    const cubeIsLight = this.isNearWhiteColor(this.currentHex, this.currentColorIndex);
    return (
      <div class={this.showSwitchLink ? 'do-colorpicker-inline' : ''}>
        <div
          class={this.showSwitchLink ? 'roughness-color-trigger' : ''}
          style={
            this.showSwitchLink
              ? { display: 'flex', alignItems: 'center', gap: '4px' }
              : undefined
          }
        >
          <div
            class={[
              'color-cube',
              this.showSwitchLink ? 'color-cube--roughness' : '',
              cubeIsLight ? 'color-cube--light' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ backgroundColor: this.currentHex ?? 'white' }}
            onClick={() => {
              this.showModal(true);
            }}
          ></div>
          {this.showSwitchLink ? (
            <span
              class="color-switch-link"
              onClick={() => {
                this.showModal(true);
              }}
            >
              切换
            </span>
          ) : null}
          <a-modal
            title="颜色选择"
            open={this.open}
            footer={null}
            onCancel={() => {
              this.showModal(false);
            }}
            class="do-colorpicker"
          >
            <p>颜色索引(ACI)</p>
            <div class="color-one-block">
              {this.firstRowData.map((item, index) => {
                return (
                  <div
                    class={`color ${this.currentHex === item.hex ? ' active' : ''}${
                      this.isNearWhiteColor(item.hex) ? ' color--light' : ''
                    }`}
                    key={index}
                    style={{ backgroundColor: item.hex }}
                    onClick={() => {
                      this.handlerClick(item, false);
                    }}
                  ></div>
                );
              })}
            </div>
            <div class="color-detail">
              <div class="detail-one">
                <span>索引颜色：</span>
                <a-input
                  style={{ width: '100px' }}
                  v-model:value={this.currentColorIndex}
                  placeholder=""
                  size="small"
                  onBlur={(e) => {
                    if (Number(e.target.value) < 0 || Number(e.target.value) > 255) {
                      this.errorMessage();
                    } else {
                      const index: number = Math.floor(e.target.value); // 用户误输入小数，直接向下取整
                      const rgbColor = CAD_COLOR_CONVERTER[index];
                      this.currentRGB = rgbColor;
                      this.currentHex = rgbToHex(rgbColor[0], rgbColor[1], rgbColor[2]);
                      this.currentR = rgbColor[0];
                      this.currentG = rgbColor[1];
                      this.currentB = rgbColor[2];
                      this.currentColorIndex = index;
                    }
                  }}
                />
              </div>
              <div>
                {/* <span class="color-rgb">RGB: {this.currentRGB?.join()}</span> */}
                <span>&nbsp;R:&nbsp;</span>
                <a-input
                  style={{ width: '50px' }}
                  v-model:value={this.currentR}
                  placeholder=""
                  size="small"
                  onBlur={(e) => {
                    if (Number(e.target.value) < 0 || Number(e.target.value) > 255) {
                      this.errorMessage();
                    } else {
                      this.currentR = Math.floor(Number(this.currentR)); // 输入框可能是string类型，需要进行一下类型转换
                    }
                  }}
                />
                <span>&nbsp;G:&nbsp;</span>
                <a-input
                  style={{ width: '50px' }}
                  v-model:value={this.currentG}
                  placeholder=""
                  size="small"
                  onBlur={(e) => {
                    if (Number(e.target.value) < 0 || Number(e.target.value) > 255) {
                      this.errorMessage();
                    } else {
                      this.currentG = Math.floor(Number(this.currentG)); // 输入框可能是string类型，需要进行一下类型转换
                    }
                  }}
                />
                <span>&nbsp;B:&nbsp;</span>
                <a-input
                  style={{ width: '50px' }}
                  v-model:value={this.currentB}
                  placeholder=""
                  size="small"
                  onBlur={(e) => {
                    if (Number(e.target.value) < 0 || Number(e.target.value) > 255) {
                      this.errorMessage();
                    } else {
                      this.currentB = Math.floor(Number(this.currentB)); // 输入框可能是string类型，需要进行一下类型转换
                    }
                  }}
                />

                <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
                <a-button
                  type="primary"
                  size="small"
                  onClick={() => {
                    // 先判断能否找到当前RGB值， 找到就直接更新，找不到就打开输入确定面板
                    const findResult: string = this.findColorIndex(
                      this.currentR,
                      this.currentG,
                      this.currentB
                    );
                    if (findResult === '') {
                      this.openRGBInput = true;
                    } else {
                      this.openRGBInput = false;
                      this.currentColorIndex = Number(findResult);
                      this.currentRGB = [this.currentR, this.currentG, this.currentB];
                      this.currentHex = rgbToHex(this.currentR, this.currentG, this.currentB);
                    }
                  }}
                >
                  确定
                </a-button>
                <a-modal
                  title="颜色未查到"
                  open={this.openRGBInput}
                  onOk={() => {
                    // 根据用户输入进行操作
                    this.handlerClick(this.inputRGB);
                    // 操作完毕设置页面关闭
                    this.openRGBInput = false;
                  }}
                  onCancel={() => {
                    // 用户取消即设置默认值，同时关闭面板
                    this.setDefaultColor();
                    this.openRGBInput = false;
                  }}
                >
                  <p>当前颜色未查到，是否需要为您匹配颜色表中最接近的颜色？</p>
                </a-modal>
              </div>
            </div>
            <div class="color-two-block">
              {this.secondRowData.map((item, index) => {
                return (
                  <div
                    class={`color ${this.currentHex === item.hex ? ' active' : ''}${
                      this.isNearWhiteColor(item.hex) ? ' color--light' : ''
                    }`}
                    key={index}
                    style={{ backgroundColor: item.hex }}
                    onClick={() => {
                      this.handlerClick(item);
                    }}
                  ></div>
                );
              })}
            </div>
            <div class="color-three-block">
              {this.thirdRowData.map((item, index) => {
                return (
                  <div
                    class={`color ${this.currentHex === item.hex ? ' active' : ''}${
                      this.isNearWhiteColor(item.hex) ? ' color--light' : ''
                    }`}
                    key={index}
                    style={{ backgroundColor: item.hex }}
                    onClick={() => {
                      this.handlerClick(item);
                    }}
                  ></div>
                );
              })}
            </div>
            <div class="confirmBtn">
              <a-button
                type="primary"
                size="small"
                onClick={() => {
                  this.confirmClick();
                }}
              >
                确定
              </a-button>
            </div>
          </a-modal>
        </div>
      </div>
    );
  },
});

export const DColorpicker: Plugin = {
  install: (app) => {
    // Plugin code goes here
    app.component('DColorpicker', comp);
  },
};
